import React from 'react';
import { ArrowDownRight, ArrowUpRight, Gauge, Minus } from 'lucide-react';
import type { IelModel } from '../../../services/ielService';
import { IEL_CLASSIFICATION_LABELS } from '../../../services/ielService';

const BAND_RING: Record<IelModel['classification'], string> = {
  excelencia: 'text-emerald-600 stroke-emerald-500',
  bom: 'text-blue-600 stroke-blue-500',
  atencao: 'text-amber-600 stroke-amber-500',
  critico: 'text-red-600 stroke-red-500',
};

const BAND_BG: Record<IelModel['classification'], string> = {
  excelencia: 'from-emerald-50 to-white dark:from-emerald-950/30 dark:to-slate-950',
  bom: 'from-blue-50 to-white dark:from-blue-950/30 dark:to-slate-950',
  atencao: 'from-amber-50 to-white dark:from-amber-950/30 dark:to-slate-950',
  critico: 'from-red-50 to-white dark:from-red-950/30 dark:to-slate-950',
};

interface TplIelHeroProps {
  iel: IelModel;
  subtitle?: string;
  compact?: boolean;
  /** Oculta textos explicativos longos — modo auditoria. */
  audit?: boolean;
}

/** Card principal IEL — Índice de Efetividade da Liderança. */
export const TplIelHero: React.FC<TplIelHeroProps> = ({ iel, subtitle, compact, audit = false }) => {
  const { score, classification, disciplina, conformidade, resultado, trend, resultadoAvailable } = iel;
  const isPartial = !resultadoAvailable;
  const circumference = 2 * Math.PI * (compact ? 36 : 42);
  const offset = circumference - (score / 100) * circumference;
  const ringCls = BAND_RING[classification];
  const size = compact ? 88 : 100;

  return (
    <div
      className={`rounded-xl border border-slate-200 bg-gradient-to-br dark:border-slate-800 ${BAND_BG[classification]} ${compact ? 'px-3 py-2' : 'px-4 py-4'}`}
    >
      <div className={`flex flex-wrap items-center ${compact ? 'gap-3' : 'gap-4'}`}>
        <div
          className="relative flex shrink-0 items-center justify-center"
          style={{ width: size, height: size }}
        >
          <svg className="-rotate-90" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle
              cx={size / 2}
              cy={size / 2}
              r={compact ? 36 : 42}
              fill="none"
              strokeWidth="8"
              className="stroke-slate-200 dark:stroke-slate-700"
            />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={compact ? 36 : 42}
              fill="none"
              strokeWidth="8"
              strokeLinecap="round"
              className={ringCls}
              strokeDasharray={circumference}
              strokeDashoffset={offset}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`font-black tabular-nums leading-none ${ringCls.split(' ')[0]} ${compact ? 'text-[22px]' : 'text-[26px]'}`}>
              {score}
            </span>
            <span className="text-[8px] font-bold uppercase tracking-wider text-slate-400">/ 100</span>
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Gauge size={16} className="text-[#2F6FED]" />
            <h4 className={`font-bold uppercase tracking-wide text-slate-800 dark:text-slate-100 ${compact ? 'text-[12px]' : 'text-[13px]'}`}>
              {isPartial ? 'IEL Parcial' : 'IEL Final'}
            </h4>
            {!audit && (
              <span className="text-[10px] font-medium text-slate-400">
                Índice de Efetividade da Liderança
              </span>
            )}
            <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ${ringCls.split(' ')[0]} bg-white/80 dark:bg-slate-900/60`}>
              {IEL_CLASSIFICATION_LABELS[classification]}
            </span>
            {isPartial && !audit && (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[9px] font-semibold text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
                Sem indicador vinculado
              </span>
            )}
            {trend != null && Math.abs(trend) >= 1 && (
              <span
                className={`flex items-center gap-0.5 text-[9px] font-semibold tabular-nums ${
                  trend > 0 ? 'text-emerald-600' : trend < 0 ? 'text-red-600' : 'text-slate-400'
                }`}
              >
                {trend > 0 ? <ArrowUpRight size={10} /> : trend < 0 ? <ArrowDownRight size={10} /> : <Minus size={10} />}
                {trend > 0 ? '+' : ''}
                {trend} p.p.
              </span>
            )}
          </div>
          {subtitle && !audit && (
            <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">{subtitle}</p>
          )}
          {isPartial && !audit && (
            <p className="mt-1 text-[10px] leading-snug text-slate-500 dark:text-slate-400">
              Calculado com Disciplina ({Math.round(iel.weights.disciplina * 100)}%) e Conformidade ({Math.round(iel.weights.conformidade * 100)}%) — resultado operacional pendente de vinculação.
            </p>
          )}
          <div className={`grid grid-cols-3 gap-x-3 ${compact || audit ? 'mt-1.5' : 'mt-3'} gap-y-1`}>
            <Pillar label="Disciplina" value={disciplina} weight={iel.weights.disciplina} />
            <Pillar label="Conformidade" value={conformidade} weight={iel.weights.conformidade} />
            <Pillar
              label="Resultado"
              value={resultado}
              weight={iel.weights.resultado}
              pending={isPartial}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const Pillar: React.FC<{ label: string; value: number | null; weight: number; pending?: boolean }> = ({
  label,
  value,
  weight,
  pending = false,
}) => (
  <div>
    <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">
      {label}
      <span className="ml-1 font-normal normal-case text-slate-300">
        {Math.round(weight * 100)}%
      </span>
    </p>
    <div className="mt-0.5 flex items-center gap-1.5">
      {pending ? (
        <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[9px] font-semibold text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
          Resultado Operacional Pendente
        </span>
      ) : value != null ? (
        <>
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
            <div
              className="h-full rounded-full bg-[#2F6FED] transition-all"
              style={{ width: `${Math.min(100, value)}%` }}
            />
          </div>
          <span className="text-[10px] font-bold tabular-nums text-slate-700 dark:text-slate-200">
            {value}%
          </span>
        </>
      ) : (
        <span className="text-[10px] font-semibold text-slate-400">N/A</span>
      )}
    </div>
  </div>
);
